/**
 * THE FLUSHER. Drains the outbox when there is a network, and never loses an item when there is not.
 * ================================================================================================
 *
 * The invariant this file must not break: **an item leaves `queued` only into `in-flight`, and leaves
 * `in-flight` only into `synced`, `failed`, or back to `queued`.** There is no path that deletes a
 * queued item, and there is no path where a thrown exception loses one — the `try/finally`-shaped
 * flow below always lands the claimed item in one of the four states.
 *
 * OUTCOME → TRANSITION. This table is the file's real content:
 *
 *   offline (before claiming)      → nothing is claimed at all. Items keep `attempts` and stay
 *                                    `queued`. Asserted by `test/sync-retry.test.ts`.
 *   auth is a stub / no session    → nothing is claimed, or the claim is RELEASED without consuming
 *                                    an attempt. A sign-in problem must not exhaust a work queue.
 *   202 / 200                      → `synced`.
 *   4xx other than 408/429         → `failed`, TERMINAL. The request is wrong and will stay wrong;
 *                                    retrying it forever would hide it. 404 and 409 land here too.
 *   408 / 429                      → retry with backoff.
 *                                    ⚠️ NOT YET IMPLEMENTED: `Retry-After` is parsed by `api.ts` on
 *                                    a 503 but is NOT used to set the schedule. Our own jittered
 *                                    backoff is used for every retryable outcome. Honouring the
 *                                    header on a 429 is the right next change (a throttle telling us
 *                                    when to come back is better information than our own guess);
 *                                    it is called out here rather than quietly assumed.
 *   5xx                            → retry with backoff.
 *   503 WITH a named blocker       → retry with backoff, and the blocker is recorded as the reason so
 *                                    the UI can say "waiting on PRD-0.12-salesforce-identity" rather
 *                                    than "sync failed". Bounded by `MAX_ATTEMPTS` like everything
 *                                    else, so it becomes visibly `failed` instead of retrying
 *                                    forever. See `api.ts` for why this is not a network error.
 *   `fetch` rejected               → retry with backoff.
 *   unknown `kind`                 → `failed` immediately. An item nothing can send must not sit in
 *                                    the queue looking like pending work.
 *
 * **408 AND 429 ARE THE ONLY RETRYABLE 4xx, AND THAT IS DELIBERATE.** Everything else in the 4xx
 * range from this API is a statement about the request: `schema-violation`,
 * `field-policy-violation`, `answers-not-flat`, `missing-or-malformed-idempotency-key`. Those cannot
 * become correct by waiting.
 *
 * ONE FLUSH AT A TIME, and one item at a time within it. Serial rather than parallel because the
 * audience is a phone on a marginal connection: six concurrent uploads on one bar of LTE is how you
 * get six timeouts instead of one success, and the queue is ordered so the technician's first
 * submission lands first.
 *
 * PHOTOS ARE A SEPARATE, LATER ITEM — because they have to be. `POST /photos/mint` verifies the
 * PARENT SUBMISSION (`submissions.get(scope, submissionId)`) before it mints anything, so a photo
 * cannot be uploaded until its submission has been accepted and has a server id. So when a submission
 * is accepted, each of its blobs is enqueued as its own `photo-upload` item, which then retries on its
 * own schedule. One flaky photo therefore cannot hold up a submission, and a submission cannot be
 * re-sent because a photo failed.
 */

import {
  createSubmission,
  isRetryable,
  mintPhotoUpload,
  putPhotoBytes,
  type ApiOutcome,
  type RequestOptions,
  type SubmissionIntakeBody,
} from './api';
import { tokenProvider } from './auth';
import { deleteBlob, getBlob } from './blobs';
import {
  dueItems,
  enqueue,
  markFailed,
  markInFlight,
  markSynced,
  recoverInFlight,
  releaseClaim,
  scheduleRetry,
  type OutboxItem,
} from './outbox';

export const KIND_SUBMISSION = 'submission';
export const KIND_PHOTO_UPLOAD = 'photo-upload';

/**
 * What the forms layer puts on the queue. The idempotency key is NOT part of this — the outbox mints
 * it, and this module reads it off the stored row.
 */
export interface SubmissionOutboxPayload {
  readonly recordType: string;
  readonly kioskId?: string | null;
  readonly ticketId?: string | null;
  /** ISO-8601 claim about when the work happened. Never presented as fact server-side (AC-3). */
  readonly capturedAt?: string | null;
  readonly answers: Readonly<Record<string, string | number | boolean | null>>;
  /** Ids from `blobs.putBlob`. Uploaded AFTER the submission is accepted — see the header. */
  readonly photoBlobIds?: readonly string[];
}

/** What this module enqueues for itself once a submission has a server id. */
export interface PhotoUploadOutboxPayload {
  readonly submissionId: string;
  readonly blobId: string;
  readonly contentType: string;
  readonly contentLength: number;
}

export interface FlushReport {
  /** Why the whole flush did nothing, if it did nothing. */
  readonly skipped?: 'offline' | 'auth-not-configured' | 'already-running' | 'nothing-due';
  readonly attempted: number;
  readonly synced: number;
  readonly failed: number;
  readonly retrying: number;
  /** Reasons, in order, for anything that did not sync. Shown in the UI, so they are sentences. */
  readonly notes: readonly string[];
}

export interface FlushOptions {
  readonly now?: number;
  readonly rng?: () => number;
  readonly fetchImpl?: typeof fetch;
  readonly onLine?: boolean;
  /** Cap per flush, so a 200-item queue does not monopolise a foreground tick. */
  readonly maxItems?: number;
}

let running = false;

export async function flushOnce(options: FlushOptions = {}): Promise<FlushReport> {
  if (running) {
    return { skipped: 'already-running', attempted: 0, synced: 0, failed: 0, retrying: 0, notes: [] };
  }
  const online = options.onLine ?? globalThis.navigator?.onLine !== false;
  if (!online) {
    // Nothing is claimed. The items are untouched — same state, same attempt count. This is the
    // "a flush while offline does not lose the item" property, and it is a property of not
    // starting rather than of unwinding correctly afterwards.
    return { skipped: 'offline', attempted: 0, synced: 0, failed: 0, retrying: 0, notes: [] };
  }
  if (tokenProvider().kind === 'stub') {
    // Checked before claiming, so a stubbed session cannot consume anybody's attempt budget.
    return { skipped: 'auth-not-configured', attempted: 0, synced: 0, failed: 0, retrying: 0, notes: [] };
  }

  running = true;
  const now = options.now ?? Date.now();
  const rng = options.rng ?? Math.random;
  const notes: string[] = [];
  let attempted = 0;
  let synced = 0;
  let failed = 0;
  let retrying = 0;

  try {
    const due = (await dueItems(now)).slice(0, options.maxItems ?? 25);
    if (due.length === 0) {
      return { skipped: 'nothing-due', attempted: 0, synced: 0, failed: 0, retrying: 0, notes: [] };
    }

    for (const queued of due) {
      const claimed = await markInFlight(queued.id);
      if (claimed === null) {
        // Another flusher (another tab) got it. Not an error.
        continue;
      }
      attempted += 1;

      let outcome: ApiOutcome<unknown>;
      try {
        outcome = await send(claimed, {
          onLine: true,
          ...(options.fetchImpl !== undefined ? { fetchImpl: options.fetchImpl } : {}),
        });
      } catch (error) {
        // A send function must not be able to strand an item in `in-flight`. Anything thrown is
        // treated as a transport failure and rescheduled.
        outcome = { kind: 'network-error', detail: error instanceof Error ? error.message : String(error) };
      }

      if (outcome.kind === 'ok' || outcome.kind === 'accepted') {
        await onSuccess(claimed, outcome.data);
        await markSynced(claimed.id);
        synced += 1;
        continue;
      }

      if (outcome.kind === 'auth-not-configured' || outcome.kind === 'unauthenticated') {
        await releaseClaim(claimed.id, 'sign-in needed before this can be sent');
        notes.push('sign-in needed');
        // Stop the flush: every remaining item would fail the same way, and hammering the
        // authorizer with a dead session is pointless load.
        break;
      }

      if (outcome.kind === 'offline') {
        // Connectivity disappeared mid-flush. Release without consuming the attempt — being
        // offline is not a failed attempt, and the item was never actually sent.
        await releaseClaim(claimed.id, 'went offline before sending');
        notes.push('went offline mid-sync');
        break;
      }

      const reason = describe(outcome);
      if (isRetryable(outcome) || isRetryableClientStatus(outcome)) {
        await scheduleRetry(claimed.id, reason, now, rng);
        retrying += 1;
      } else {
        await markFailed(claimed.id, reason);
        failed += 1;
      }
      notes.push(reason);
    }
  } finally {
    running = false;
  }

  return { attempted, synced, failed, retrying, notes };
}

/** 408 and 429 only. See the header for why nothing else in the 4xx range comes back. */
function isRetryableClientStatus(outcome: ApiOutcome<unknown>): boolean {
  return outcome.kind === 'rejected' && (outcome.status === 408 || outcome.status === 429);
}

/** Human-readable, and it names the blocker when there is one. Shown to a technician. */
function describe(outcome: ApiOutcome<unknown>): string {
  switch (outcome.kind) {
    case 'not-built-yet':
      // The blocker id is the point: "waiting on C-12-technician-attribution" is actionable for an
      // operator, "sync failed" is not.
      return `waiting on an unfinished dependency: ${outcome.blocker}`;
    case 'server-error':
      return `server error (HTTP ${outcome.status})`;
    case 'network-error':
      return `network problem: ${outcome.detail}`;
    case 'offline':
      return 'offline';
    case 'rejected':
      return `rejected by the server: ${outcome.code}`;
    case 'not-found':
      return 'the server has no such record (or it is not this supplier’s)';
    case 'conflict':
      return `idempotency conflict: ${outcome.code}`;
    case 'unauthenticated':
      return 'not signed in';
    case 'auth-not-configured':
      return 'sign-in is not built yet';
    case 'ok':
    case 'accepted':
      return 'ok';
  }
}

/** Dispatch by `kind`. An unknown kind is a terminal failure, not a silent skip. */
async function send(item: OutboxItem, options: RequestOptions): Promise<ApiOutcome<unknown>> {
  if (item.kind === KIND_SUBMISSION) {
    const payload = item.payload as SubmissionOutboxPayload;
    const body: SubmissionIntakeBody = {
      recordType: payload.recordType,
      // THE STORED KEY. Never regenerated — this line is the whole idempotency guarantee, and
      // `test/sync-retry.test.ts` captures it across two attempts to prove it.
      idempotencyKey: item.idempotencyKey,
      answers: payload.answers,
      ...(payload.kioskId !== undefined ? { kioskId: payload.kioskId } : {}),
      ...(payload.ticketId !== undefined ? { ticketId: payload.ticketId } : {}),
      ...(payload.capturedAt !== undefined ? { capturedAt: payload.capturedAt } : {}),
    };
    return createSubmission(body, options);
  }

  if (item.kind === KIND_PHOTO_UPLOAD) {
    return sendPhoto(item.payload as PhotoUploadOutboxPayload, options);
  }

  return {
    kind: 'rejected',
    status: 400,
    code: `unknown-outbox-kind:${item.kind}`,
    requestId: null,
  };
}

/**
 * Mint, then PUT. Two calls, and the mint is repeated on every attempt on purpose.
 *
 * A minted URL is short-lived, so caching one across a backoff window would produce an expired
 * signature and a 403 that reads like a permissions failure. Minting again is one cheap authorized
 * call; the C-39 ledger records each mint, which is exactly the audit trail that should exist.
 */
async function sendPhoto(
  payload: PhotoUploadOutboxPayload,
  options: RequestOptions,
): Promise<ApiOutcome<unknown>> {
  const blob = await getBlob(payload.blobId);
  if (blob === undefined) {
    // The bytes are gone — evicted, or already cleaned up. Terminal and LOUD: this is the one place
    // where a photo can be genuinely lost, and it must not read as a transient sync problem.
    return {
      kind: 'rejected',
      status: 410,
      code: 'photo-bytes-missing-from-device',
      requestId: null,
    };
  }

  const minted = await mintPhotoUpload(
    {
      submissionId: payload.submissionId,
      contentType: payload.contentType,
      contentLength: blob.size,
    },
    options,
  );
  if (minted.kind !== 'ok') {
    return minted;
  }

  const put = await putPhotoBytes(minted.data, blob, {
    ...(options.fetchImpl !== undefined ? { fetchImpl: options.fetchImpl } : {}),
  });
  return put;
}

/**
 * After a successful send. Enqueues photo uploads, and only then may bytes be dropped.
 *
 * Ordering matters: the photo items are enqueued (and committed) BEFORE the submission is marked
 * `synced` by the caller, so there is no window in which the submission is done and its photos are
 * not queued.
 */
async function onSuccess(item: OutboxItem, data: unknown): Promise<void> {
  if (item.kind === KIND_PHOTO_UPLOAD) {
    const payload = item.payload as PhotoUploadOutboxPayload;
    // The bytes have landed in S3. Only now are they safe to drop from the device.
    await deleteBlob(payload.blobId);
    return;
  }

  if (item.kind !== KIND_SUBMISSION) {
    return;
  }
  const payload = item.payload as SubmissionOutboxPayload;
  const blobIds = payload.photoBlobIds ?? [];
  if (blobIds.length === 0) {
    return;
  }
  const submissionId = (data as { submissionId?: unknown } | null)?.submissionId;
  if (typeof submissionId !== 'string' || submissionId.length === 0) {
    // Accepted with no id we can anchor a photo on. The photos stay on the device rather than being
    // dropped, and this is surfaced rather than swallowed.
    // eslint-disable-next-line no-console -- an unreachable-looking branch that must not be silent.
    console.error('[lockercustody] submission accepted without a submissionId; photos NOT queued');
    return;
  }

  for (const blobId of blobIds) {
    const blob = await getBlob(blobId);
    if (blob === undefined) {
      continue;
    }
    await enqueue<PhotoUploadOutboxPayload>(KIND_PHOTO_UPLOAD, {
      submissionId,
      blobId,
      contentType: blob.type,
      contentLength: blob.size,
    });
  }
}

// ---------------------------------------------------------------------------
// The scheduler
// ---------------------------------------------------------------------------

const POLL_INTERVAL_MS = 30_000;

/**
 * Start the background flusher. Returns a stop function.
 *
 * FOUR TRIGGERS, and each one exists because the others miss a real case:
 *  · `online`            — the obvious one, and the least reliable: iOS Safari fires it late, or not
 *                          at all when a VPN or captive portal is involved.
 *  · `visibilitychange`  — a technician bringing the app back to the foreground is the single most
 *                          common moment for connectivity to have changed underneath it. iOS
 *                          freezes timers in a backgrounded tab, so this is the trigger that
 *                          actually fires after a van drives into coverage.
 *  · a 30s poll          — covers `online` not firing at all, and covers a backoff coming due while
 *                          the app sits in the foreground.
 *  · startup             — after `recoverInFlight()`, which is what unsticks an item stranded by
 *                          the app being killed mid-request.
 *
 * `flushOnce` guards itself against overlap, so all four firing at once is harmless.
 */
export function startSync(options: FlushOptions = {}): () => void {
  let stopped = false;
  const tick = (): void => {
    if (stopped) {
      return;
    }
    void flushOnce(options).catch(() => undefined);
  };

  const onOnline = (): void => tick();
  const onVisible = (): void => {
    if (globalThis.document?.visibilityState === 'visible') {
      tick();
    }
  };

  globalThis.addEventListener?.('online', onOnline);
  globalThis.document?.addEventListener('visibilitychange', onVisible);
  const timer = globalThis.setInterval(tick, POLL_INTERVAL_MS);

  void recoverInFlight()
    .then(() => tick())
    .catch(() => tick());

  return () => {
    stopped = true;
    globalThis.removeEventListener?.('online', onOnline);
    globalThis.document?.removeEventListener('visibilitychange', onVisible);
    globalThis.clearInterval(timer);
  };
}
